import { getAuth } from 'firebase/auth';
import { collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, Timestamp, where } from 'firebase/firestore';
import { IFutureOrder } from '../sp-types/FutureOrderType';
import { StatusType } from '../sp-types/statusType';
import { db } from './firebase';
import { deleteItem, saveItem, updateItem } from './genericFB';
import { saveFutureOrderExposures } from './futureOrderExposureFB';

const FUTURE_ORDER_LOCATION = 'future-orders';
const DB = db;

export const deleteFutureOrders = async (accountId: string) => {
  const futureOrdersRef = collection(DB, FUTURE_ORDER_LOCATION);
  const q = query(futureOrdersRef, where('accountId', '==', accountId));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    console.log('No matching documents.');
    return;
  }
  for (const aDoc of querySnapshot.docs) {
    await deleteDoc(aDoc.ref);
  }
  console.log(`Deleted ${querySnapshot.size} documents where accountId == ${accountId}`);
};

export const deleteFutureOrder = async (uuid: string) => {
  await deleteItem(uuid, FUTURE_ORDER_LOCATION);
};

export async function getAllFutureOrdersByAccount(
  accountId: string,
  statusTypes: StatusType[],
): Promise<IFutureOrder[] | null> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    console.error('User is not authenticated');
    return null;
  }

  const futureOrdersRef = collection(DB, FUTURE_ORDER_LOCATION);
  try {
    const q = query(futureOrdersRef, where('accountId', '==', accountId), where('status', 'in', statusTypes));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const futureOrders = querySnapshot.docs.map(
        (doc) =>
          ({
            ...doc.data(),
            uuid: doc.id, // Add the Firestore document ID to the future order object
          } as IFutureOrder),
      );
      return futureOrders;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting documents:', error);
    return null;
  }
}

export async function createFutureOrder(
  accountId: string,
  futureOrder: Partial<IFutureOrder>,
): Promise<{ uuid: string; status: string } | null> {
  try {
    const futureOrdersCollection = collection(DB, FUTURE_ORDER_LOCATION);
    const docRef = futureOrder.uuid ? doc(futureOrdersCollection, futureOrder.uuid) : doc(futureOrdersCollection);

    const futureOrderWithAccountId = {
      ...futureOrder,
      accountId: accountId,
      uuid: docRef.id,
    };

    await setDoc(docRef, futureOrderWithAccountId, { merge: true });
    console.log('Document written with ID: ', docRef.id);
    if (docRef.id && futureOrder.status === 'completed' && futureOrder.totalExposure) {
      await saveFutureOrderExposures({ ...futureOrderWithAccountId, uuid: docRef.id } as IFutureOrder);
    }
    // Return the UUID and a success status message after successful save
    return { uuid: docRef.id, status: 'success' };
  } catch (e) {
    console.error('Error adding document: ', e);
    return { uuid: '', status: 'error' }; // Return an error status if the operation fails
  }
}

export const saveExistingFutureOrder = async (futureOrder: IFutureOrder): Promise<IFutureOrder | null> => {
  if (futureOrder.uuid) {
    const result = await saveItem<IFutureOrder>(futureOrder.uuid, futureOrder, FUTURE_ORDER_LOCATION);
    if (result) {
      const results = await saveFutureOrderExposures(futureOrder);
      console.log('saved exposures', results);
    }
    return result;
  }
  return null;
};

export const updateFutureOrder = async (uuid: string, data: Partial<IFutureOrder>): Promise<IFutureOrder | null> => {
  const updateFO = await updateItem<IFutureOrder>(uuid, data, FUTURE_ORDER_LOCATION);
  if (updateFO) {
    await saveFutureOrderExposures(updateFO);
  }
  return updateFO;
};

export async function getFutureOrderById(uuid: string): Promise<IFutureOrder | null> {
  try {
    const futureOrderDocRef = doc(DB, FUTURE_ORDER_LOCATION, uuid);
    const futureOrderDoc = await getDoc(futureOrderDocRef);
    if (futureOrderDoc.exists()) {
      return {
        ...futureOrderDoc.data(),
        updatedAt: futureOrderDoc.data().updatedAt || Timestamp.now(),
        futureOrderDate: futureOrderDoc.data().futureOrderDate || Timestamp.now(),
        uuid: futureOrderDoc.id,
      } as IFutureOrder;
    } else {
      console.log(`No future order found with ID: ${uuid}`);
      return null;
    }
  } catch (error) {
    console.error('Error fetching future order:', error);
    return null;
  }
}
